import { hasValue } from 'swipeclock-frontend';

// The browser starts `timeout` only after location permission is granted, so
// an unanswered permission prompt keeps getGeoLocation pending until answered.
const GEO_OPTIONS = {
  enableHighAccuracy: true,
  timeout: 5000,
};

const POSITION_UNAVAILABLE = 2;

const geoErrorResult = (message, code) => ({
  geoError: {
    message,
    code,
  },
});

/**
 * Read a previously captured mobile geolocation result.
 * Returns exactly one of geoLocation or geoError, or null when neither is present.
 *
 * @param {Object} source
 * @returns {{ geoLocation: Object }|{ geoError: Object }|null}
 */
export const readStoredGeoResult = (source) => {
  if (!source || typeof source !== 'object') {
    return null;
  }

  if (hasValue(source.geoLocation)) {
    return { geoLocation: source.geoLocation };
  }

  if (hasValue(source.geoError)) {
    return { geoError: source.geoError };
  }

  return null;
};

/**
 * Copy a captured geolocation result onto a failed finalize error so a queue
 * fallback can persist the same coordinates or error.
 *
 * @param {Object} error
 * @param {{ geoLocation?: Object, geoError?: Object }} geoResult
 * @returns {Object}
 */
export const attachGeoResult = (error, geoResult) => {
  const stored = readStoredGeoResult(geoResult);

  if (!stored || !error || typeof error !== 'object') {
    return error;
  }

  if (stored.geoLocation) {
    error.geoLocation = stored.geoLocation;
    return error;
  }

  error.geoError = stored.geoError;
  return error;
};

/**
 * Collect geolocation for a mobile punch when clock-connect requires it.
 * Resolves to { geoLocation }, { geoError }, or {} and never rejects.
 *
 * @param {boolean} isMobileMode
 * @param {boolean} geoLocationRequired
 * @returns {Promise<{ geoLocation?: Object, geoError?: Object }>}
 */
export const getGeoLocation = async (isMobileMode, geoLocationRequired) => {
  if (isMobileMode !== true || geoLocationRequired !== true) {
    return {};
  }

  if (typeof window !== 'undefined' && window.isSecureContext === false) {
    return geoErrorResult(
      'Geolocation requires a secure context',
      POSITION_UNAVAILABLE
    );
  }

  const geolocation =
    typeof navigator !== 'undefined' ? navigator.geolocation : undefined;

  if (!geolocation || typeof geolocation.getCurrentPosition !== 'function') {
    return geoErrorResult('Geolocation is not available', POSITION_UNAVAILABLE);
  }

  return new Promise((resolve) => {
    geolocation.getCurrentPosition(
      (position) => {
        try {
          const { latitude, longitude, accuracy } = position.coords;
          resolve({
            geoLocation: {
              latitude: latitude.toString(),
              longitude: longitude.toString(),
              accuracy: accuracy.toString(),
            },
          });
        } catch (error) {
          resolve(
            geoErrorResult(
              error?.message || 'Unable to read geolocation',
              POSITION_UNAVAILABLE
            )
          );
        }
      },
      (error) => {
        resolve(
          geoErrorResult(
            error?.message || 'Unable to retrieve geolocation',
            error?.code
          )
        );
      },
      GEO_OPTIONS
    );
  });
};
